import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, Loader2, XCircle, Crown, Sparkles } from "lucide-react";

const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2000;

export default function SubscriptionSuccessScreen() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("pending"); // pending | paid | failed | expired | timeout
  const [info, setInfo] = useState(null);
  const attemptRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptRef.current += 1;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (cancelled) return;
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          await refresh();
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState(data.status === "expired" ? "expired" : "failed");
          return;
        }
      } catch {
        if (cancelled) return;
        setState("failed");
        return;
      }
      if (attemptRef.current >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <Shell section="subscription">
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center fade-up">
        {state === "pending" && (
          <>
            <Loader2 size={56} className="text-[#4CAF50] animate-spin mb-5" />
            <h1 className="text-2xl font-bold mb-2 text-shadow-green">Confirming your payment…</h1>
            <p className="text-soft text-sm max-w-md">
              Stripe is finalising your subscription. This usually takes just a couple of seconds.
            </p>
          </>
        )}

        {state === "paid" && (
          <div className="glass-card-strong p-8 max-w-lg w-full" data-testid="payment-success" style={{ borderColor: "rgba(76,175,80,0.5)", boxShadow: "0 0 28px rgba(76,175,80,0.35)" }}>
            <div className="relative inline-flex mb-5">
              <CheckCircle2 size={68} className="text-[#4CAF50]" style={{ filter: "drop-shadow(0 0 18px rgba(76,175,80,0.7))" }} />
              <Sparkles size={20} className="text-[#FFD54F] absolute -top-2 -right-2 animate-pulse" />
            </div>
            <h1 className="text-3xl font-extrabold text-shadow-green">Welcome to {(info?.tier || "Premium").replace(/^./, (c) => c.toUpperCase())}!</h1>
            <p className="text-soft mt-3 leading-relaxed">
              Your payment of <b className="text-white">${info?.amount?.toFixed(2)} {info?.currency?.toUpperCase()}</b> was successful.
              All {info?.tier} features are now unlocked on your account.
            </p>
            <div className="flex gap-3 mt-7">
              <button onClick={() => navigate("/home")} className="btn-primary flex-1" data-testid="success-go-home">
                <Crown size={14} className="inline mr-1.5 -mt-0.5" /> Explore my plan
              </button>
              <button onClick={() => navigate("/profile")} className="btn-outline flex-1" data-testid="success-go-profile">
                View profile
              </button>
            </div>
          </div>
        )}

        {(state === "failed" || state === "expired") && (
          <div className="glass-card-strong p-8 max-w-lg w-full" data-testid="payment-failed" style={{ borderColor: "rgba(211,47,47,0.5)" }}>
            <XCircle size={56} className="text-[#FF6B6B] mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">{state === "expired" ? "Session expired" : "Payment not completed"}</h1>
            <p className="text-soft text-sm">
              {state === "expired"
                ? "Your checkout session expired before payment finished. Please start the upgrade again."
                : "We couldn't confirm your payment. If you were charged, contact support@careforflora.app — we'll sort it out."}
            </p>
            <button onClick={() => navigate("/subscription")} className="btn-primary mt-6" data-testid="payment-retry">Try again</button>
          </div>
        )}

        {state === "timeout" && (
          <div className="glass-card-strong p-8 max-w-lg w-full" data-testid="payment-timeout">
            <Loader2 size={48} className="text-[#FF8F00] mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Still confirming</h1>
            <p className="text-soft text-sm">
              Stripe is taking longer than usual. Your subscription will activate automatically once the payment clears — check your profile in a minute or two.
            </p>
            <button onClick={() => navigate("/profile")} className="btn-outline mt-6" data-testid="payment-check-profile">Go to profile</button>
          </div>
        )}
      </div>
    </Shell>
  );
}
